import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtDate, formatApiError, todayLocal, attachmentUrl } from "@/lib/api";
import { loadExpenseCategories } from "@/lib/categories";
import ListSearch, { filterBy } from "@/components/ListSearch";
import PartyPicker from "@/components/PartyPicker";

import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, DollarSign, FileCog, Pencil, Copy, Repeat, ScanLine, Sparkles, Loader2, Paperclip, ExternalLink, Receipt as ReceiptIcon } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import FileUpload from "@/components/FileUpload";
import CurrencyRateInput from "@/components/CurrencyRateInput";
import BulkActionBar from "@/components/BulkActionBar";
import ItemPickerCombo from "@/components/ItemPickerCombo";
import TidyUpDialog from "@/components/TidyUpDialog";
import BillPaymentsDialog from "@/components/BillPaymentsDialog";
import { toast } from "sonner";

const STATUS_STYLES = {
    paid: "bg-emerald-50 text-emerald-700",
    open: "bg-secondary/10 text-secondary-dark",
    overdue: "bg-coral/10 text-coral-dark",
    partial: "bg-amber-50 text-amber-700",
};

/**
 * Build a one-line description from an invoice/bill's line items. Used by the
 * list-view "Description" column so users can see what a doc is for without
 * opening it. Joins line descriptions with " · ", truncates to ~60 chars.
 */
function linesSummary(lines, limit = 60) {
    if (!Array.isArray(lines) || lines.length === 0) return "";
    const joined = lines
        .map((ln) => (ln?.description || "").trim())
        .filter(Boolean)
        .join(" · ");
    if (!joined) return "";
    if (joined.length <= limit) return joined;
    return joined.slice(0, limit - 1).trimEnd() + "…";
}

/** Build the search-filter spec for bill list rows. */
const BILL_SEARCH_FIELDS = [
    "number", "vendor_name", "status", "currency",
    (b) => (b.lines || []).map((ln) => ln?.description || "").join(" "),
    "notes",
];

export default function Bills() {
    const { currentOrg } = useAuth();
    const [items, setItems] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [accounts, setAccounts] = useState([]);  // full CoA for item→category resolution
    const [catalog, setCatalog] = useState([]);    // purchase / both items
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [payOpen, setPayOpen] = useState(null);
    const [scanOpen, setScanOpen] = useState(false);
    const [scanFile, setScanFile] = useState(null);
    const [scanning, setScanning] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [bulkBusy, setBulkBusy] = useState(false);
    const [tidyOpen, setTidyOpen] = useState(false);
    const [paymentsOpen, setPaymentsOpen] = useState(null);
    const today = todayLocal();
    const blank = { vendor_id: "", issue_date: today, due_date: today, currency: currentOrg?.currency || "KYD",
        fx_rate: 1, notes: "", attachment_id: "", lines: [{ description: "", quantity: 1, unit_price: 0, tax_rate: 0, category: "" }],
        is_recurring: false, recurrence: { frequency: "monthly", interval: 1, next_run: today, end_date: "" } };
    const [form, setForm] = useState(blank);
    const [q, setQ] = useState("");

    const load = async () => {
        const [{ data: b }, { data: v }, { data: ba }, { data: cat }, { data: acc }, cats] = await Promise.all([
            api.get("/bills"),
            api.get("/vendors"),
            api.get("/bank-accounts").catch(() => ({ data: { items: [] } })),
            api.get("/items").catch(() => ({ data: { items: [] } })),
            api.get("/accounts").catch(() => ({ data: { items: [] } })),
            loadExpenseCategories(),
        ]);
        setItems(b.items || []);
        setVendors(v.items || []);
        setBankAccounts(ba.items || []);
        // Only purchase / both items are relevant for Bills; hide archived/inactive.
        setCatalog((cat.items || []).filter((i) => i.item_type !== "sales" && i.active !== false));
        setAccounts(acc.items || []);
        setCategories(cats);
    };
    useEffect(() => { if (currentOrg) load(); }, [currentOrg]);

    const totals = useMemo(() => {
        let sub = 0, tax = 0;
        for (const ln of form.lines) {
            const amt = Number(ln.quantity || 0) * Number(ln.unit_price || 0);
            sub += amt;
            if (!currentOrg?.cayman_no_tax_mode) tax += amt * (Number(ln.tax_rate || 0) / 100);
        }
        return { sub, tax, total: sub + tax };
    }, [form.lines, currentOrg]);

    const setLine = (idx, k, v) => {
        const lines = [...form.lines];
        lines[idx] = { ...lines[idx], [k]: v };
        setForm({ ...form, lines });
    };

    const submit = async () => {
        try {
            const recurrence = form.is_recurring ? {
                frequency: form.recurrence.frequency,
                interval: Number(form.recurrence.interval) || 1,
                next_run: form.recurrence.next_run || today,
                end_date: form.recurrence.end_date || null,
                active: true,
            } : null;
            const payload = { ...form, recurrence };
            delete payload.is_recurring;
            if (editing) {
                await api.patch(`/bills/${editing.id}`, payload);
                toast.success("Bill updated");
            } else {
                await api.post("/bills", payload);
                toast.success("Bill created");
            }
            setOpen(false); setEditing(null); setForm(blank); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const duplicate = async (id) => {
        try {
            const { data } = await api.post(`/bills/${id}/duplicate`);
            toast.success(`Duplicated as ${data.number}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const openCreate = () => {
        setEditing(null);
        setForm(blank);
        setOpen(true);
    };

    const openEdit = (b) => {
        setEditing(b);
        setForm({
            vendor_id: b.vendor_id,
            issue_date: b.issue_date,
            due_date: b.due_date,
            currency: b.currency,
            fx_rate: b.fx_rate || 1,
            notes: b.notes || "",
            attachment_id: b.attachment_id || "",
            lines: (b.lines || []).map((l) => ({
                description: l.description || "",
                quantity: l.quantity,
                unit_price: l.unit_price,
                tax_rate: l.tax_rate || 0,
                category: l.category || "",
            })),
            is_recurring: !!b.recurrence?.active,
            recurrence: {
                frequency: b.recurrence?.frequency || "monthly",
                interval: b.recurrence?.interval || 1,
                next_run: b.recurrence?.next_run || today,
                end_date: b.recurrence?.end_date || "",
            },
        });
        setOpen(true);
    };

    const pay = async (id, { amount, paidOn, accountId, method, reference, attachmentId }) => {
        try {
            await api.post(`/bills/${id}/pay`, {
                amount: Number(amount),
                method: method || "manual",
                paid_on: paidOn || null,
                account_id: accountId || null,
                reference: reference || null,
                attachment_id: attachmentId || null,
            });
            toast.success("Bill paid"); setPayOpen(null); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async (id) => {
        const bill = items.find((b) => b.id === id);
        const paidAmt = Number(bill?.amount_paid || 0);
        const payWarning = paidAmt > 0
            ? `\n\nThis bill has ${fmtMoney(paidAmt, bill.currency)} in recorded payment(s). Deleting it will also PERMANENTLY DELETE those payment records and reverse them from your bank ledger.`
            : "";
        if (!window.confirm(`Delete bill ${bill?.number || ""}?${payWarning}\n\nThis action cannot be undone.`)) return;
        try {
            const { data } = await api.delete(`/bills/${id}`);
            if (data?.payments_deleted > 0) toast.success(`Bill deleted (${data.payments_deleted} payment${data.payments_deleted === 1 ? "" : "s"} also removed)`);
            else toast.success("Bill deleted");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const toggleSel = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const bulkDelete = async () => {
        const ids = Array.from(selected);
        if (!ids.length) return;
        const paidCount = ids.filter((id) => Number(items.find((b) => b.id === id)?.amount_paid || 0) > 0).length;
        const warn = paidCount > 0 ? `\n\n${paidCount} of these bills have recorded payments that will ALSO be deleted.` : "";
        if (!window.confirm(`Delete ${ids.length} bill${ids.length === 1 ? "" : "s"}?${warn}\n\nThis action cannot be undone.`)) return;
        setBulkBusy(true);
        const results = await Promise.allSettled(ids.map((id) => api.delete(`/bills/${id}`)));
        const failed = results.filter((r) => r.status === "rejected").length;
        const ok = results.length - failed;
        const paymentsDeleted = results
            .filter((r) => r.status === "fulfilled")
            .reduce((acc, r) => acc + (r.value?.data?.payments_deleted || 0), 0);
        if (ok) toast.success(`${ok} bill${ok === 1 ? "" : "s"} deleted${paymentsDeleted ? ` (${paymentsDeleted} linked payment${paymentsDeleted === 1 ? "" : "s"} also removed)` : ""}`);
        if (failed) toast.error(`${failed} bill${failed === 1 ? "" : "s"} could not be deleted`);
        setSelected(new Set());
        setBulkBusy(false);
        load();
    };

    const runScan = async () => {
        if (!scanFile?.id) {
            toast.error("Upload a bill file first");
            return;
        }
        setScanning(true);
        try {
            const { data } = await api.post("/bills/scan", { attachment_id: scanFile.id });
            const d = data.draft || {};
            // If the scan created a new vendor, append it to our list.
            if (data.vendor_was_created && data.vendor) {
                setVendors((prev) => [...prev, data.vendor]);
                toast.success(`New vendor created: ${data.vendor.name}`);
            } else if (data.vendor) {
                toast.success(`Matched vendor: ${data.vendor.name}`);
            }
            setEditing(null);
            setForm({
                vendor_id: d.vendor_id || "",
                issue_date: d.issue_date || today,
                due_date: d.due_date || today,
                currency: d.currency || (currentOrg?.currency || "KYD"),
                fx_rate: d.fx_rate ?? 1,
                notes: d.notes || "",
                attachment_id: d.attachment_id || scanFile?.id || "",
                lines: (d.lines && d.lines.length ? d.lines : [{ description: "", quantity: 1, unit_price: 0, tax_rate: 0, category: "" }]),
                is_recurring: false,
                recurrence: { frequency: "monthly", interval: 1, next_run: today, end_date: "" },
            });
            setScanOpen(false);
            setScanFile(null);
            setOpen(true);
            if (d.scanned_invoice_number) {
                toast(`Scanned bill number: ${d.scanned_invoice_number}`);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setScanning(false);
        }
    };

    const currency = currentOrg?.currency || "KYD";

    return (
        <div>
            <PageHeader
                subtitle="Purchases"
                title="Bills"
                action={
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setTidyOpen(true)}
                            className="rounded-xl border-secondary/40 text-secondary-dark hover:bg-secondary/10"
                            data-testid="bills-tidy-up-btn"
                            title="Find and clean duplicate bills"
                        >
                            <Sparkles className="h-4 w-4 mr-1.5" /> Tidy up
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => { setScanFile(null); setScanOpen(true); }}
                            className="rounded-xl border-secondary/40 text-secondary-dark hover:bg-secondary/10"
                            data-testid="scan-bill-btn"
                        >
                            <ScanLine className="h-4 w-4 mr-1.5" /> Scan bill
                        </Button>
                        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(blank); } }}>
                            <DialogTrigger asChild><Button onClick={openCreate} className="bg-ocean-700 text-white rounded-xl" data-testid="new-bill-btn"><Plus className="h-4 w-4 mr-1.5" /> New bill</Button></DialogTrigger>
                        <DialogContent className="max-w-2xl">
                            <DialogHeader><DialogTitle className="font-heading text-2xl">{editing ? `Edit bill${editing.number ? ` · ${editing.number}` : ""}` : "New bill"}</DialogTitle></DialogHeader>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <div>
                                    <Label>Vendor</Label>
                                    <PartyPicker
                                        kind="vendor"
                                        options={vendors}
                                        value={form.vendor_id}
                                        onChange={(v) => setForm({ ...form, vendor_id: v })}
                                        onCreated={(nv) => setVendors([...vendors, nv])}
                                        placeholder="Select vendor"
                                        testId="bill-vendor-select"
                                    />
                                </div>
                                <div><Label>Issue date</Label><Input className="mt-1.5" type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></div>
                                <div><Label>Due date</Label><Input className="mt-1.5" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
                            </div>
                            <div className="mt-4">
                                <CurrencyRateInput
                                    baseCurrency={currentOrg?.currency || "KYD"}
                                    currency={form.currency}
                                    fxRate={form.fx_rate}
                                    amount={totals.total}
                                    onCurrency={(c) => setForm({ ...form, currency: c })}
                                    onFxRate={(r) => setForm({ ...form, fx_rate: r })}
                                    testId="bill-fx"
                                />
                            </div>
                            {form.attachment_id && (
                                <div className="mt-4 flex items-center justify-between rounded-2xl border border-secondary/30 bg-secondary/5 px-4 py-2.5 text-sm" data-testid="bill-attached-file">
                                    <div className="flex items-center gap-2 text-secondary-dark">
                                        <Paperclip className="h-4 w-4" />
                                        <span className="font-medium">Scanned bill attached</span>
                                        <span className="text-muted-foreground">will be saved with this bill</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <a
                                            href={attachmentUrl(form.attachment_id)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 text-secondary-dark hover:text-ocean-700 text-xs font-medium"
                                            data-testid="bill-attachment-preview"
                                        >
                                            Preview <ExternalLink className="h-3 w-3" />
                                        </a>
                                        <button
                                            type="button"
                                            onClick={() => setForm({ ...form, attachment_id: "" })}
                                            className="ml-2 text-xs text-muted-foreground hover:text-coral-dark"
                                            data-testid="bill-attachment-remove"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            )}
                            <div className="mt-4">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Line items</div>
                                    {catalog.length > 0 && (
                                        <span className="text-[11px] text-muted-foreground">Tip: pick a purchase item to auto-fill</span>
                                    )}
                                </div>
                                <div className="grid grid-cols-12 gap-2 px-1 pb-1.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                                    <div className="col-span-5">Description</div>
                                    <div className="col-span-2">Qty</div>
                                    <div className="col-span-2">Unit price</div>
                                    <div className="col-span-3">Category</div>
                                </div>
                                {form.lines.map((ln, i) => (
                                    <div key={i} className="grid grid-cols-12 gap-2 mb-2">
                                        <div className="col-span-5 flex gap-1.5">
                                            <Input className="flex-1" placeholder="Description" value={ln.description} onChange={(e) => setLine(i, "description", e.target.value)} data-testid={`bill-line-desc-${i}`} />
                                            <ItemPickerCombo
                                                items={catalog}
                                                filterType="purchase"
                                                currency={form.currency}
                                                testId={`bill-line-${i}-item`}
                                                currentRate={ln.unit_price}
                                                currentTaxRate={currentOrg?.cayman_no_tax_mode ? 0 : ln.tax_rate}
                                                onCreate={(it) => setCatalog((prev) => [...prev, it])}
                                                onPick={(it) => {
                                                    // Resolve the item's linked expense account -> category name
                                                    const linkedAcct = accounts.find((a) => a.id === it.account_id && a.type === "expense");
                                                    const lines = [...form.lines];
                                                    lines[i] = {
                                                        ...lines[i],
                                                        description: it.name + (it.description ? ` — ${it.description}` : ""),
                                                        unit_price: it.rate || 0,
                                                        tax_rate: currentOrg?.cayman_no_tax_mode ? 0 : (it.tax_rate || 0),
                                                        item_id: it.id,
                                                        category: linkedAcct ? linkedAcct.name : (lines[i].category || ""),
                                                    };
                                                    setForm({ ...form, lines });
                                                }}
                                            />
                                        </div>
                                        <Input className="col-span-2" type="number" placeholder="Qty" value={ln.quantity} onChange={(e) => setLine(i, "quantity", Number(e.target.value))} />
                                        <Input className="col-span-2" type="number" placeholder="Unit price" value={ln.unit_price} onChange={(e) => setLine(i, "unit_price", Number(e.target.value))} />
                                        <Select value={ln.category || ""} onValueChange={(v) => setLine(i, "category", v)}>
                                            <SelectTrigger className="col-span-3" data-testid={`bill-line-category-${i}`}><SelectValue placeholder="Category" /></SelectTrigger>
                                            <SelectContent>
                                                {Array.from(new Set([...(ln.category ? [ln.category] : []), ...categories])).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                ))}
                                <div className="mt-2 rounded-xl bg-sand-100 p-3 flex justify-between text-sm">
                                    <div className="text-muted-foreground">Total</div>
                                    <div className="font-mono">{fmtMoney(totals.total, form.currency)}</div>
                                </div>
                            </div>
                            <div className="mt-5 rounded-2xl border border-sand-300 p-4">
                                <label className="flex items-start gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={form.is_recurring}
                                        onChange={(e) => setForm({ ...form, is_recurring: e.target.checked })}
                                        data-testid="bill-recurring-toggle"
                                        className="mt-1 h-4 w-4 accent-secondary"
                                    />
                                    <div>
                                        <div className="inline-flex items-center gap-2 font-medium text-ocean-700">
                                            <Repeat className="h-4 w-4 text-secondary" /> Make this bill recurring
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-0.5">
                                            Auto-create the next bill on the schedule you set (rent, subscriptions, utilities).
                                        </div>
                                    </div>
                                </label>
                                {form.is_recurring && (
                                    <div className="mt-4 grid grid-cols-2 gap-3">
                                        <div>
                                            <Label className="text-xs">Frequency</Label>
                                            <Select value={form.recurrence.frequency} onValueChange={(v) => setForm({ ...form, recurrence: { ...form.recurrence, frequency: v } })}>
                                                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="weekly">Weekly</SelectItem>
                                                    <SelectItem value="monthly">Monthly</SelectItem>
                                                    <SelectItem value="quarterly">Quarterly</SelectItem>
                                                    <SelectItem value="yearly">Yearly</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="text-xs">Every</Label>
                                            <Input type="number" min="1" className="mt-1.5" value={form.recurrence.interval} onChange={(e) => setForm({ ...form, recurrence: { ...form.recurrence, interval: e.target.value } })} />
                                        </div>
                                        <div>
                                            <Label className="text-xs">Next run</Label>
                                            <Input type="date" className="mt-1.5" value={form.recurrence.next_run} onChange={(e) => setForm({ ...form, recurrence: { ...form.recurrence, next_run: e.target.value } })} />
                                        </div>
                                        <div>
                                            <Label className="text-xs">Ends on (optional)</Label>
                                            <Input type="date" className="mt-1.5" value={form.recurrence.end_date} onChange={(e) => setForm({ ...form, recurrence: { ...form.recurrence, end_date: e.target.value } })} />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} className="bg-ocean-700 text-white" data-testid="submit-bill-btn">{editing ? "Save changes" : "Create"}</Button></DialogFooter>
                        </DialogContent>
                    </Dialog>
                    </div>
                }
            />
            {items.length === 0 ? (
                <EmptyState icon={FileCog} title="No bills yet" description="Track what you owe vendors in one place." />
            ) : (
                <>
                    <BulkActionBar
                        count={selected.size}
                        label="bill"
                        onDelete={bulkDelete}
                        onClear={() => setSelected(new Set())}
                        busy={bulkBusy}
                        warning="Linked payments will also be deleted."
                        testId="bill-bulk"
                    />
                    <div className="mb-4 flex items-center gap-3">
                        <ListSearch value={q} onChange={setQ} placeholder="Search bills by number, vendor, description or status…" testId="bills-search" />
                        {q && <span className="text-xs text-muted-foreground">{filterBy(items, q, BILL_SEARCH_FIELDS).length} of {items.length}</span>}
                    </div>
                    <div className="rounded-3xl bg-white border border-sand-200 shadow-card overflow-hidden">
                        <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 [scrollbar-width:thin] relative">
                            <table className="w-full text-sm min-w-[720px] sm:min-w-0">
                                <thead className="bg-sand-100 text-xs uppercase text-muted-foreground">
                                    <tr>
                                        <th className="px-3 py-3 w-8">
                                            <input
                                                type="checkbox"
                                                checked={items.length > 0 && selected.size === filterBy(items, q, BILL_SEARCH_FIELDS).length}
                                                onChange={(e) => {
                                                    if (e.target.checked) setSelected(new Set(filterBy(items, q, BILL_SEARCH_FIELDS).map((b) => b.id)));
                                                    else setSelected(new Set());
                                                }}
                                                className="h-4 w-4 accent-secondary cursor-pointer"
                                                data-testid="bills-select-all"
                                                aria-label="Select all bills"
                                            />
                                        </th>
                                        <th className="text-left px-5 py-3">Bill #</th>
                                        <th className="text-left px-5 py-3">Vendor</th>
                                        <th className="text-left px-5 py-3">Description</th>
                                        <th className="text-left px-5 py-3">Due</th>
                                        <th className="text-right px-5 py-3">Total</th>
                                        <th className="text-right px-5 py-3">Balance</th>
                                        <th className="text-left px-5 py-3">Status</th>
                                        <th className="px-5 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-sand-200">
                                    {filterBy(items, q, BILL_SEARCH_FIELDS).map((b) => (
                                        <tr key={b.id} className={`hover:bg-sand-50/50 ${selected.has(b.id) ? "bg-secondary/5" : ""}`}>
                                            <td className="px-3 py-3 text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={selected.has(b.id)}
                                                    onChange={() => toggleSel(b.id)}
                                                    className="h-4 w-4 accent-secondary cursor-pointer"
                                                    data-testid={`bill-select-${b.id}`}
                                                    aria-label={`Select bill ${b.number}`}
                                                />
                                            </td>
                                            <td className="px-5 py-3 font-mono text-xs">{b.number}</td>
                                            <td className="px-5 py-3 font-medium text-ocean-700">{b.vendor_name}</td>
                                            <td className="px-5 py-3 text-muted-foreground max-w-[260px] truncate" title={(b.lines || []).map((ln) => ln?.description || "").filter(Boolean).join(" · ") || b.notes || ""} data-testid={`bill-description-${b.id}`}>
                                                {linesSummary(b.lines) || <span className="text-muted-foreground/60 italic">—</span>}
                                            </td>
                                            <td className="px-5 py-3 text-muted-foreground">{fmtDate(b.due_date)}</td>
                                            <td className="px-5 py-3 font-mono text-right">
                                                <div className="inline-flex items-center gap-2 justify-end">
                                                    {b.currency && b.currency !== currency && (
                                                        <span className="text-[10px] font-medium tracking-wide text-secondary-dark bg-secondary/10 rounded-md px-1.5 py-0.5" data-testid={`bill-fx-chip-${b.id}`} title={`Stored rate: 1 ${b.currency} = ${(b.fx_rate || 1).toFixed(4)} ${currency}`}>
                                                            {b.currency} · {(b.fx_rate || 1).toFixed(2)}
                                                        </span>
                                                    )}
                                                    <span>{fmtMoney(b.total, b.currency)}</span>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3 font-mono text-right">{fmtMoney(b.balance, b.currency)}</td>
                                            <td className="px-5 py-3"><Badge className={`border-0 ${STATUS_STYLES[b.status] || ""}`}>{b.status}</Badge></td>
                                            <td className="px-5 py-3 text-right">
                                                {b.attachment_id && (
                                                    <a
                                                        href={attachmentUrl(b.attachment_id)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center mr-1.5 text-secondary-dark hover:text-ocean-700"
                                                        title="View attached bill"
                                                        data-testid={`bill-attachment-${b.id}`}
                                                    >
                                                        <Paperclip className="h-4 w-4" />
                                                    </a>
                                                )}
                                                {b.recurrence?.active && (
                                                    <span title={`Recurring ${b.recurrence.frequency}`} className="inline-flex items-center mr-1.5 text-secondary" data-testid={`recurring-badge-${b.id}`}>
                                                        <Repeat className="h-4 w-4" />
                                                    </span>
                                                )}
                                                {b.status !== "void" && <Button variant="ghost" size="sm" onClick={() => openEdit(b)} data-testid={`edit-bill-${b.id}`} title="Edit"><Pencil className="h-4 w-4 text-muted-foreground" /></Button>}
                                                <Button variant="ghost" size="sm" onClick={() => duplicate(b.id)} data-testid={`duplicate-bill-${b.id}`} title="Duplicate"><Copy className="h-4 w-4 text-muted-foreground" /></Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setPaymentsOpen(b)}
                                                    data-testid={`view-payments-${b.id}`}
                                                    title={Number(b.amount_paid || 0) > 0
                                                        ? "View / edit payments & receipts"
                                                        : "No payments yet"}
                                                    disabled={Number(b.amount_paid || 0) === 0}
                                                    className={Number(b.amount_paid || 0) === 0 ? "opacity-40" : ""}
                                                >
                                                    <ReceiptIcon className="h-4 w-4 text-muted-foreground" />
                                                </Button>
                                                {b.status !== "paid" && <Button variant="ghost" size="sm" onClick={() => setPayOpen(b)} data-testid={`pay-bill-${b.id}`}><DollarSign className="h-4 w-4" /></Button>}
                                                <Button variant="ghost" size="sm" onClick={() => remove(b.id)}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            <Dialog open={!!payOpen} onOpenChange={(v) => !v && setPayOpen(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Pay bill</DialogTitle></DialogHeader>
                    {payOpen && <BillPayForm bill={payOpen} bankAccounts={bankAccounts} today={today} onPay={(data) => pay(payOpen.id, data)} />}
                </DialogContent>
            </Dialog>

            <BillPaymentsDialog
                bill={paymentsOpen}
                onClose={() => setPaymentsOpen(null)}
                onChanged={load}
            />

            <Dialog open={scanOpen} onOpenChange={(v) => { setScanOpen(v); if (!v) { setScanFile(null); } }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl flex items-center gap-2">
                            <Sparkles className="h-5 w-5 text-secondary" /> Scan bill with AI
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Upload a supplier bill (PDF or image). BlueLedger will read it with GPT-4o Vision,
                            match the vendor (or create a new one), and pre-fill a draft bill for your review.
                        </p>
                        <FileUpload
                            value={scanFile}
                            onChange={setScanFile}
                            testId="scan-bill-upload"
                            label="Upload vendor bill (PDF or image, up to 5 MB)"
                        />
                        <div className="rounded-2xl bg-sand-100 p-3 text-xs text-muted-foreground">
                            Tip: clear scans and straight PDFs work best. The file will be attached to the
                            created bill for audit.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setScanOpen(false)} disabled={scanning}>Cancel</Button>
                        <Button
                            onClick={runScan}
                            disabled={!scanFile?.id || scanning}
                            className="bg-ocean-700 text-white"
                            data-testid="scan-bill-submit"
                        >
                            {scanning ? (<><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Scanning…</>) : (<><Sparkles className="h-4 w-4 mr-1.5" /> Scan &amp; open draft</>)}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <TidyUpDialog
                open={tidyOpen}
                onOpenChange={setTidyOpen}
                label="bill"
                title="Tidy up duplicate bills"
                fetchUrl="/bills/duplicate-groups"
                mergeUrl={null}
                deleteUrlPrefix="/bills/"
                onDone={load}
                renderRow={(m, isCanonical) => (
                    <div>
                        <div className={`font-medium text-sm ${isCanonical ? "text-secondary-dark" : "text-ocean-700"}`}>
                            {m.number} · {m.vendor_name}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                            Issued {fmtDate(m.issue_date)} · Due {fmtDate(m.due_date)}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                            {fmtMoney(m.total, m.currency)} · {m.status}
                            {Number(m.amount_paid || 0) > 0 && (
                                <span className="ml-1 text-secondary-dark">· {fmtMoney(m.amount_paid, m.currency)} paid — will cascade delete its payment(s)</span>
                            )}
                        </div>
                    </div>
                )}
            />
        </div>
    );
}

function BillPayForm({ bill, bankAccounts, today, onPay }) {
    const [amt, setAmt] = useState(bill.balance.toString());
    const [paidOn, setPaidOn] = useState(today);
    const [method, setMethod] = useState("manual");
    const [accountId, setAccountId] = useState(bankAccounts?.[0]?.id || "");
    const [reference, setReference] = useState("");
    const [attachment, setAttachment] = useState(null);
    return (
        <div className="space-y-4">
            <div className="rounded-2xl bg-sand-100 p-4 text-sm">
                <div className="text-muted-foreground text-xs uppercase">{bill.number}</div>
                <div className="font-medium text-ocean-700 mt-1">{bill.vendor_name}</div>
                <div className="mt-2 text-muted-foreground text-xs">Balance: <span className="font-mono">{fmtMoney(bill.balance, bill.currency)}</span></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div><Label>Amount</Label><Input type="number" value={amt} onChange={(e) => setAmt(e.target.value)} className="mt-1.5" data-testid="bill-pay-amount" /></div>
                <div><Label>Date paid</Label><Input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} className="mt-1.5" data-testid="bill-pay-date" /></div>
                <div>
                    <Label>Method</Label>
                    <Select value={method} onValueChange={setMethod}>
                        <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="manual">Cash / manual</SelectItem>
                            <SelectItem value="bank">Bank transfer</SelectItem>
                            <SelectItem value="card">Card</SelectItem>
                            <SelectItem value="cheque">Cheque</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label>Paid from account</Label>
                    <Select value={accountId} onValueChange={setAccountId}>
                        <SelectTrigger className="mt-1.5" data-testid="bill-pay-account">
                            <SelectValue placeholder={bankAccounts?.length ? "Select account" : "No bank accounts"} />
                        </SelectTrigger>
                        <SelectContent>
                            {(bankAccounts || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div>
                <Label>Reference (optional)</Label>
                <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. cheque #1234" className="mt-1.5" />
            </div>
            <FileUpload
                value={attachment}
                onChange={setAttachment}
                testId="bill-pay-attachment"
                onScan={(parsed) => {
                    if (parsed.total != null && parsed.total > 0) setAmt(String(parsed.total));
                    if (parsed.date) setPaidOn(parsed.date);
                    if (parsed.merchant && !reference) setReference(parsed.merchant);
                }}
            />
            <Button onClick={() => onPay({ amount: amt, paidOn, accountId, method, reference, attachmentId: attachment?.id })} className="w-full bg-ocean-700 text-white" data-testid="bill-pay-submit">Pay</Button>
        </div>
    );
}
